/**
 * 删除购物车 Item
 */
if (!customElements.get('cart-remove')) {
  customElements.define('cart-remove', class CartRemove extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('click', (event) => {
        event.preventDefault();
        
        // 获取行号，确保是数字或有效字符串
        const index = this.dataset.index;
        if (!index) return;

        // 尝试查找最近的 cart-items 组件
        let cartItems = this.closest('cart-items');
        
        // 补救措施：如果 closest 找不到（例如 HTML 结构被修改），尝试通过 ID 或全局查找
        if (!cartItems) {
           // 尝试查找当前 section 下的 cart-items
           const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
           if (sectionId) {
             cartItems = document.querySelector(`cart-items[data-section="${sectionId}"]`);
           }
           // 如果还找不到，找页面上第一个
           if (!cartItems) {
             cartItems = document.querySelector('cart-items');
           }
        }

        // 情况 A: 找到了组件且方法可用
        if (cartItems && typeof cartItems.updateQuantity === 'function') {
          cartItems.updateQuantity(index, 0);
        } 
        // 情况 B: 彻底找不到，使用备用方案
        else {
          console.warn('未找到 cart-items 组件，使用备用删除逻辑');
          this.removeItemDirectly(index);
        }
      });
    }

    // 备用方法：直接调用 API 删除，然后刷新页面
    removeItemDirectly(index) {
      this.classList.add('loading'); 

      const body = JSON.stringify({
        line: index,
        quantity: 0
      });

      fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      })
      .then((response) => response.json())
      .then((state) => {
        window.location.reload();
      })
      .catch((error) => {
        console.error('删除购物车商品出错:', error);
        this.classList.remove('loading'); // 出错时移除 loading
      });
    }
  });
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', class CartItems extends HTMLElement {
    constructor() {
      super();
      this.debounceTimers = {};
      
      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);

      this.addEventListener('keydown', (event) => {
        // 检查是否按下了 Enter 键
        if (event.code === 'Enter' || event.key === 'Enter') {
          // 阻止默认的表单提交行为（即阻止跳转到 Checkout）
          event.preventDefault();
          
          // 手动让当前输入框失去焦点
          // 这会自动触发上面的 'change' 事件，从而执行更新数量的逻辑
          if (event.target instanceof HTMLInputElement) {
            event.target.blur();
          }
        }
      });

      this.sectionId = this.dataset.section;
      this.inDrawer = this.hasAttribute('data-in-drawer');
      
      // 修复 ID 选择器：确保这里的 ID 与 Liquid 模板中渲染的 ID 完全一致
      // 通常 Dawn 默认为 Cart-Items-SECTION_ID
      this.cartItemsContainer = document.getElementById(`Cart-Items-${this.sectionId}`);
      this.lineItemStatusElement = document.getElementById(`Cart-Line-Item-Status-${this.sectionId}`);

      if (this.inDrawer) {
        this.drawerTrap = this.closest('[data-trap]');
      }

      // 初始化检查礼品包装
      if (this.hasAttribute('data-gift-wrap-id')) {
        const giftWrapQty = parseInt(this.dataset.giftWrapQuantity);
        const itemQty = parseInt(this.dataset.itemQuantity);
        if (!isNaN(giftWrapQty) && !isNaN(itemQty) && giftWrapQty !== itemQty) {
          this.updateQuantity(this.dataset.giftWrapIndex, itemQty, null, this.dataset.giftWrapId, true);
        }
      }
    }

    onChange(event) {
      // 安全检查：如果输入框被清空，视为 0 或者不处理（这里设为 0 会触发删除，或者你可以 return 等待用户输入）
      if (event.target.value === '') return;

      this.updateQuantity(
        event.target.dataset.index, 
        event.target.value, 
        event.target, 
        event.target.dataset.quantityVariantId
      );
    }

    updateQuantity(line, quantity, focusableElement, variantId, immediate = false) {
      // 清除旧定时器
      if (this.debounceTimers[line]) {
        clearTimeout(this.debounceTimers[line]);
      }

      // 如果数量小于 0，修正为 0
      if (parseInt(quantity) < 0) quantity = 0;

      if (immediate) {
        this.executeFetch(line, quantity, focusableElement, variantId);
      } else {
        // 这里的 300ms 比较合适，400ms 略长会让用户觉得反应慢
        this.debounceTimers[line] = setTimeout(() => {
          this.executeFetch(line, quantity, focusableElement, variantId);
        }, 300);
      }
    }

    executeFetch(line, quantity, focusableElement, variantId) {
      delete this.debounceTimers[line];
      this.enableLoading(line);

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      fetch(window['routes']['cart_change_url'], { ...fetchConfig(), body })
        .then((response) => response.json())
        .then((responseJson) => {
          if (responseJson.errors) {
            this.disableLoading(line);
            this.restoreLineItemQuantityInputs(line);
            return this.showError(responseJson.errors);
          }

          // 礼品包装逻辑
          const giftWrapLine = this.dataset.giftWrapIndex;
          const giftWrapping = document.getElementById(`Cart-Gift-Wrapping-${this.sectionId}`);

          if (giftWrapping && giftWrapLine != null && String(giftWrapLine) !== String(line)) {
            const item_count = responseJson['item_count']; 
            const currentGiftQty = parseInt(this.dataset.giftWrapQuantity) || 0;
            // 假设逻辑：礼品包装数量 = 购物车总数 - 礼品包装本身的数量 (避免循环计算)
            // 这里的业务逻辑请根据你的实际需求确认
            const item_quantity = item_count - currentGiftQty;

            if (item_quantity > 0) {
              this.updateQuantity(giftWrapLine, item_quantity, focusableElement, variantId, true);
            } else {
              // 如果没有其他商品，移除包装
              if (typeof giftWrapping.removeWrapping === 'function') {
                 giftWrapping.removeWrapping();
              }
              this.handleCartUpdate(responseJson, variantId, focusableElement); 
              this.disableLoading(line);
            }
          } else {
            this.handleCartUpdate(responseJson, variantId, focusableElement);
            this.disableLoading(line);
          }
        })
        .catch((error) => {
          console.error(error);
          this.disableLoading(line);
          this.restoreLineItemQuantityInputs(line); // 出错时重置输入框
          this.showError(window['cartStrings']['error']);
        });
    }

    handleCartUpdate(responseJson, variantId, focusableElement) {
      SectionDynamicUpdate.updateSections(this.getSectionsToRender(), responseJson.sections);

      // 重新获取焦点逻辑优化
      let newFocusTarget = null;
      
      if (focusableElement) {
        // 1. 尝试通过 ID 找
        if (focusableElement.id) {
          newFocusTarget = document.getElementById(focusableElement.id);
        }
        // 2. 如果 ID 找不到（可能 ID 变了），尝试通过 dataset.index 和 name 属性找
        if (!newFocusTarget && focusableElement.dataset.index) {
           newFocusTarget = this.querySelector(
             `.quantity-input[data-index="${focusableElement.dataset.index}"]`
           );
        }
      }

      if (this.inDrawer && this.drawerTrap && typeof trapFocus === 'function') {
        trapFocus(this.drawerTrap, newFocusTarget || this.querySelector('[tabindex="-1"]'));
      } else if (newFocusTarget) {
        newFocusTarget.focus();
        // 某些浏览器 focus 后光标会在最前面，这里尝试把光标移到最后
        if (newFocusTarget.value) {
            const val = newFocusTarget.value;
            newFocusTarget.value = '';
            newFocusTarget.value = val;
        }
      }

      // 发布事件供其他组件监听 (例如头部购物车气泡)
      if (window.PUB_SUB_EVENTS && window.publish) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          productVariantId: variantId,
          cartData: responseJson,
        });
      }
    }

    restoreLineItemQuantityInputs(line) {
      if (!line) return;
      const quantityInputs = this.querySelectorAll(`.quantity-input[data-index='${line}']`);
      quantityInputs.forEach(input => {
        input.value = input.defaultValue || input.getAttribute('value');
      });
    }

    getSectionsToRender() {
      const sections = [
        {
          id: 'Cart-Icon-Bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        }
      ];

      if (this.inDrawer) {
        sections.push({
          id: 'Cart-Drawer',
          section: this.sectionId,
          selector: '#Cart-Drawer-Details',
        });
      } else {
        sections.push({
          id: 'Main-Cart',
          section: this.sectionId,
          selector: '#Main-Cart-Details',
        });
      }
      return sections;
    }

    showError(message) {
      if (!message) return;
      if (typeof popToast === 'function') {
        popToast(message, 'error');
      } else {
        alert(message);
      }
    }

    enableLoading(line) {
      if (this.cartItemsContainer) {
        this.cartItemsContainer.classList.add('cart-items--disabled');
      }

      // 优化选择器逻辑
      if (line) {
        const cartItemElement = this.querySelector(`#Cart-Item-${this.sectionId}-${line}`);
        if (cartItemElement) cartItemElement.classList.add('loading');
      }
      
      if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', "false");
    }

    disableLoading(line) {
      if (this.cartItemsContainer) {
        this.cartItemsContainer.classList.remove('cart-items--disabled');
      }

      if (line) {
        const cartItemElement = this.querySelector(`#Cart-Item-${this.sectionId}-${line}`);
        if (cartItemElement) cartItemElement.classList.remove('loading');
      }

      if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', "true");
    }
  });
}

if(!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', class FreeShippingProgress extends HTMLElement {
    /**
     * data-total-amount 购物车总价金额，单位分
     * data-free-threshold 包邮门槛金额，单位元
     * data-confetti 触发彩带庆祝效果
     */
    constructor() {
      super();

      this.renderProgress();
    }

    get progressBar() {
      return this.querySelector('.free-shipping-progress');
    }

    get messageWrapper () {
      return this.querySelector('.free-shipping-message');
    }

    /**
     * 渲染包邮进度条
     */
    renderProgress() {
      const cartTotalAmount = this.dataset.totalAmount;
      const freeThreshold = this.dataset.freeThreshold;

      if(!isNumeric(cartTotalAmount) || !isNumeric(freeThreshold)) return;

      const freeThresholdLocal = Math.round(parseInt(freeThreshold) * (Shopify.currency.rate || 1)) * 100; // 门槛金额汇率转换，单位分

      let progress;
      if(cartTotalAmount - freeThresholdLocal >= 0) {
        progress = 100;
        this.messageWrapper.innerHTML = window['cartStrings']['freeShippingUnlockedMessage'];
        this.classList.add('free-has-unlocked');
      } else {
        progress = cartTotalAmount * 100 / freeThresholdLocal;

        const amountToQualify = freeThresholdLocal - cartTotalAmount;
        this.messageWrapper.innerHTML = window['cartStrings']['freeShippingLockedMessage'].replace('[amount]', formatPriceAmount(amountToQualify, Shopify.currency.active));
      }

      // 进度条进度
      if(this.progressBar) this.progressBar.style.setProperty('--free-shipping-progress', `${progress}%`);

      // 触发庆祝彩带特效
      if(this.hasAttribute('data-confetti')) {
        document.dispatchEvent(new CustomEvent('freeShippingUnlocked', {
          detail: { status: progress >= 100 }
        }));
      }
    }
  });
}

/**
 * 订单备注
 */
if (!customElements.get('cart-note-modal')) {
  customElements.define(
      'cart-note-modal',
      class CartNoteModal extends ModalDialog {
        constructor() {
          super();

          const form  = this.querySelector('form');
          if(!form) return;

          this.submitButton = this.querySelector('button[type=submit]');
          form.addEventListener('submit', this.formSubmit.bind(this));
        }

        /**
         * 处理保存备注
         * @param event
         */
        formSubmit(event) {
          event.preventDefault();
          this.startLoading();

          const formData = new FormData(event.target);
          const formDataJson = Object.fromEntries(formData.entries());
          const body = JSON.stringify(formDataJson);

          fetch(window['routes']['cart_update_url'], { ...fetchConfig(), ...{ body } })
              .then(response => {
                return response.json();
              })
              .then(response=>{
                if(response.error) {
                  throw new Error('Cart update error');
                }

                popToast(window['cartStrings']['addToNoteSuccess'], 'success');
                this.hide();
              }).catch(error=> {
                popToast(window['cartStrings']['addToNoteError'], 'error');
              }).finally(()=>{
                this.endLoading();
              });
        }

        startLoading() {
          this.submitButton.classList.add('loading');
          this.submitButton.setAttribute('disabled', 'disabled');
        }

        endLoading() {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('disabled');
        }
      }
  );
}

/**
 * 订单运费预估
 */
if(!customElements.get('shipping-calculator-modal')){
  customElements.define('shipping-calculator-modal', class ShippingCalculatorModal extends ModalDialog {

    static MAX_ATTEMPTS = 3;
    constructor() {
      super();

      this.asyncAttemptCount = 0; // Async请求尝试次数

      const form = this.querySelector('form');
      if(!form) return;

      form.addEventListener('submit', this.prepareRate.bind(this));

      this.submitButton = this.querySelector('button[type=submit]');
      this.successMessageElement = this.querySelector('.alert-success');
    }

    /**
     * 请求查询物流信息
     * @param event
     */
    prepareRate(event){
      event.preventDefault();

      this.hideResults();
      this.startLoading();

      const formData = new FormData(event.target);
      const shipping_address = {
        country: formData.get('country'),
        province: formData.get('province'),
        zip: formData.get('zip')
      };
      const body = JSON.stringify({ shipping_address: shipping_address });

      fetch(`${window['routes']['cart_url']}/prepare_shipping_rates.json`, {...fetchConfig(), ...{ body }})
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Network response was not ok: ${response.statusText}`);
            }
            return response.json();
          })
          .then((response)=>{
            if(response == null || response.ok){
              this.asyncRate({ shipping_address: shipping_address });
            }else {
              throw new Error('Prepare shipping rates error');
            }
          }).catch(error=>{
            this.showError();
          });
    }

    /**
     * 异步查询返回的信息
     * @param params
     */
    asyncRate(params){
      fetch(`${window['routes']['cart_url']}/async_shipping_rates.json?` + this.serialize(params), {...fetchConfig('json', 'GET')})
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if(response != null && response['shipping_rates'] != null && response['shipping_rates'].length > 0) {
              this.showResults(response);
            } else {
              if(++this.asyncAttemptCount >= ShippingCalculatorModal.MAX_ATTEMPTS){
                this.showError();
              }  else {
                setTimeout(()=>this.asyncRate(params), 3000);
              }
            }
          }).catch(error=>{
            this.showError();
          });
    }

    startLoading(){
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('disabled', 'disabled');
    }

    endLoading(){
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('disabled');
    }

    showError(){
      this.endLoading();
      popToast(window['cartStrings']['estimateShippingError'], 'error');
    }

    /**
     * 显示查询到的结果
     * @param result
     */
    showResults(result){
      this.endLoading();

      const rates = result['shipping_rates'];
      if(!rates || rates.length <= 0 || !this.successMessageElement) return;

      // 标题修改
      const countElement = this.successMessageElement.querySelector('.result-count');
      countElement.innerText = rates.length;

      // 显示查询到的物流列表
      const template = this.successMessageElement.querySelector('template').content; // 消息模板
      const fragment = document.createDocumentFragment();
      rates.forEach((rate)=>{
        const clone = document.importNode(template, true);

        clone.querySelector('.presentment-name').textContent = rate['presentment-name'] || rate['name'];
        clone.querySelector('.delivery-price').textContent = rate['price'] + rate['currency'];

        let deliveryDate = rate['delivery_date'];
        if(rate['delivery_range'] && Array.isArray(rate['delivery_range'])) {
          if(rate['delivery_range'].length > 1) {
            deliveryDate = rate['delivery_range'].join(' - ');
          }else {
            deliveryDate = rate['delivery_range'][0];
          }
        }

        if(deliveryDate == null || deliveryDate === '') {
          deliveryDate = 'unknown';
        }
        clone.querySelector('.delivery-date').textContent = deliveryDate;

        fragment.appendChild(clone);
      });

      this.successMessageElement.querySelector('.message-list').appendChild(fragment);

      this.successMessageElement.removeAttribute('hidden');
      this.endLoading();
    }

    hideResults() {
      if(this.successMessageElement) {
        this.successMessageElement.querySelector('.message-list').innerHTML = '';
        this.successMessageElement.setAttribute('hidden', 'true');
      }
    }

    serialize(obj, prefix) {
      const str = Object.keys(obj)
          .filter(key => obj.hasOwnProperty(key))
          .map(key => {
            const k = prefix ? `${prefix}[${key}]` : key;
            const v = obj[key];
            return (v !== null && typeof v === "object") ?
                this.serialize(v, k) :
                `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          });
      return str.join("&");
    }
  });
}

/**
 * 礼品包装
 */
if(!customElements.get('gift-wrapping')) {
  customElements.define('gift-wrapping', class GiftWrapping extends HTMLElement {
    constructor() {
      super();

      this.giftWrapId = this.dataset.giftWrappingId;
      if(!this.giftWrapId) return;
      this.itemQuantity = Math.max(0, parseInt(this.dataset.itemQuantity)); // 订单中的item总数量

      this.cartItems = document.getElementById(`Cart-Items-Main-${this.dataset.section}`);

      const boundChange = debounce(this.onChange.bind(this), 1000);
      this.checkInput = this.querySelector('input[type="checkbox"]');
      this.checkInput.addEventListener('change', boundChange);
    }

    onChange(event) {
      if(event.target.checked) {
        this.setWrapping();
      } else {
        this.removeWrapping();
      }
    }

    /**
     * 添加包裹
     */
    setWrapping () {
      const body = JSON.stringify({
        updates: {
          [this.giftWrapId]: this.itemQuantity
        },
        attributes: {
          "gift-wrap": "Yes"
        },
        sections: this.cartItems.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      this.fetchData(body);
    }

    /**
     * 移除包裹
     */
    removeWrapping() {
      const body = JSON.stringify({
        updates: {
          [this.giftWrapId]: 0
        },
        attributes: {
          "gift-wrap": ""
        },
        sections: this.cartItems.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      this.fetchData(body);
    }

    fetchData(body) {
      this.enableLoading();
      fetch(window['routes']['cart_update_url'], { ...fetchConfig(), body })
          .then(response => {
            return response.json();
          })
          .then(responseJson=>{
            this.cartItems.handleCartUpdate(responseJson, this.giftWrapId, this.checkInput);
          }).catch(error=> {
            popToast(window['variantStrings']['unknownError'], 'error');
          }).finally(()=>{
            this.disableLoading();
          });
    }

    enableLoading() {
      this.cartItems.enableLoading();

      this.checkInput.setAttribute('disabled', 'true');
    }

    disableLoading() {
      this.cartItems.disableLoading();

      this.checkInput.removeAttribute('disabled');
    }
  });
}